<template>
  <Tag v-show="emptyShow !== false || getBindValue.text" class="mts-base-tag" v-bind="getBindValue">
    <template v-for="item in Object.keys($slots)" #[item]="data">
      <slot :name="item" v-bind="data || {}"></slot>
    </template>

    {{ getBindValue.text ?? emptyShow }}
  </Tag>
</template>

<script setup lang="ts">
  import { Tag, TagProps } from 'ant-design-vue';
  import { computed, useAttrs } from 'vue';
  import type { CellProps } from '@mts/components/Table';
  import type { Option } from '@mts-fe/types';
  import type { MColor } from '..';

  defineOptions({
    name: 'BaseTag',
    inheritAttrs: false,
  });

  export interface BaseTagProps
    extends Partial<CellProps>, /* @vue-ignore */ Omit<TagProps, 'status'> {
    value?: unknown;
    options?: (Option & { color?: MColor | TagProps['color'] })[];
    textMode?: boolean;
    bordered?: boolean;
    emptyShow?: false | '-';
  }
  const props = withDefaults(defineProps<BaseTagProps>(), {
    options: () => [],
    textMode: false,
    bordered: false,
    emptyShow: '-',
  });

  const attrs = useAttrs();
  const getBindValue = computed(() => {
    const { text, record, column, index, value, options, textMode, ...restProps } = props;
    const curItem = options.find((i) => i.value == text?.toString());
    const color = (curItem?.color || attrs.color || 'default') as string;

    return {
      ...attrs,
      ...restProps,
      class: [
        attrs.class,
        'ant-tag-' + color,
        {
          'ant-tag-text-mode': textMode,
          'mts-tag-bordered': props.bordered,
        },
      ],
      color,
      text: (curItem?.label || value || text) as string,
    };
  });
</script>

<style lang="less">
  @import (reference) '@mts/design/color.less';

  @mts-bg-color-map: {
    temp: #f4ecff;
    check: #fff7e5;
    todo: #ebf1ff;
    pre_doing: #f6ffeb;
    doing: #fff1ea;
    pre_done: #fff7e5;
    pub: #e9fcf8;
    done: #f6f7f8;
    close: #f6f7f8;
    cancel: #f6f7f8;
    error: #fff0f0;
    mian_liao: #e3fafc;
    fu_liao: #fdeff5;
  };

  .mts-base-tag.ant-tag {
    each(@mts-color-map, { 
      &.ant-tag-@{key}{ 
        color: @value;
      }
    });
    each(@mts-bg-color-map, { 
      &.ant-tag-@{key}{
        border-color: @value;
        background-color: @value; 
      }
    });

    padding: 2px 4px;
    line-height: @line-height;

    &.mts-tag-bordered {
      border-color: currentcolor;
      background-color: #fff !important;
    }

    &.ant-tag-text-mode {
      background-color: transparent;
    }
  }

  .mts-pad .mts-base-tag.ant-tag {
    min-width: 26px;
    padding: 3px;
    text-align: center;
  }
</style>
